import { useState, useRef, useEffect, useCallback } from 'react';

import type { SeturInputProps } from '@components/Input';

import {
  InputLabel,
  SeturInput,
  InputContainer,
  isStyledComponent,
  styled,
  InputWrapper,
  IconContainer,
} from './styled';

const UseInputAPI = ({
  getInputAPI,
  applyValue,
  inputRef,
}: Pick<SeturInputProps, 'getInputAPI'> & {
  applyValue: (v: string) => void;
  inputRef: SeturInputProps['inputRef'];
}) => {
  const reset = useCallback(() => {
    setTimeout(() => {
      applyValue('');
      if (inputRef?.current) inputRef.current.value = '';
    }, 0);
  }, [inputRef, applyValue]);
  useEffect(() => {
    getInputAPI?.({ applyValue, reset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};
const Input = ({
  className,
  error,
  label,
  width,
  icon = null,
  placeholder,
  suffix,
  value,
  onChange,
  useNative,
  labelFocusOpacity,
  height = 68,
  labelTop = 15,
  disabled,
  InputComponent = SeturInput,
  inputRef,
  getInputAPI,
  inputDisable,
  tcknType,
  iconLeft = false,
  ...rest
}: React.PropsWithChildren<SeturInputProps>) => {
  const [currentValue, setCurrentValue] = useState(value || rest.defaultValue);
  const defaultRefInputRef: NonNullable<typeof inputRef> = useRef(null);
  const actualInputRef = inputRef || defaultRefInputRef;
  const InputRef = useRef(
    isStyledComponent(InputComponent)
      ? InputComponent
      : styled(InputComponent)``
  );
  useEffect(() => {
    if (rest.defaultValue) {
      setCurrentValue(rest.defaultValue);
      onChange?.(rest.defaultValue as string);
    }
  }, []);

  return (
    <>
      {getInputAPI ? (
        <UseInputAPI
          getInputAPI={getInputAPI}
          applyValue={setCurrentValue}
          inputRef={actualInputRef}
        />
      ) : null}
      <InputContainer
        className={className}
        hasError={error}
        width={width}
        value={value || currentValue}
        useNative={useNative}
        focusOpacity={labelFocusOpacity}
        height={height}
        labelTop={labelTop}
        disabled={tcknType === 'tckn' ? inputDisable : disabled}
        InputComponent={InputRef.current}
      >
        <InputWrapper>
          {(icon || suffix) && iconLeft ? (
            <IconContainer>{icon ?? suffix}</IconContainer>
          ) : null}
          <InputComponent
            {...rest}
            value={value}
            disabled={tcknType === 'tckn' ? inputDisable : disabled}
            onChange={(e) => {
              const {
                target: { value },
              } = e;
              onChange?.(value as string);
              setCurrentValue(value);
            }}
            placeholder={useNative ? placeholder : ''}
            ref={actualInputRef}
            autoComplete='off'
          />
          {(icon || suffix) && !iconLeft ? (
            <IconContainer>{icon ?? suffix}</IconContainer>
          ) : null}
        </InputWrapper>
        <InputLabel data-role='label'>{placeholder ?? label}</InputLabel>
      </InputContainer>
    </>
  );
};

export default Input;

export { InputContainer };
